import type { QueryKey } from '@tanstack/react-query'

import type { UUID } from '@/types/Id'
import type {
  MonitorFeedEntryStatus,
  MonitorFeedSort,
  MonitorRecipeTarget,
} from '@/types/Monitor'

interface OrganizationMonitorsQueryKeyArgs {
  orgId: UUID | ''
}

interface MonitorQueryKeyArgs {
  monitorId: UUID | ''
}

// Membership filter for one cached feed-list page — shared by the org-feed
// page-data helpers in `feedCache.ts` (e.g. `FeedListMembershipFilter` adds
// `bookmarked`). Not a query-key shape; just the fields an optimistic patch
// needs to decide whether a mutated row stays in a given cached list.
export interface MonitorFeedListFilter {
  status?: MonitorFeedEntryStatus
}

/**
 * Org-wide feed filter — the full server-param surface of the org feed. `sort`
 * is always present (defaults to 'recent'); the rest are optional facets that
 * omit the corresponding query param when undefined.
 */
export interface OrgFeedListFilter {
  status?: MonitorFeedEntryStatus
  monitorId?: UUID
  bookmarked?: boolean
  resourceKind?: string
  q?: string
  sort: MonitorFeedSort
}

interface OrgFeedListQueryKeyArgs
  extends OrganizationMonitorsQueryKeyArgs, OrgFeedListFilter {}

interface MonitorFeedDetailQueryKeyArgs extends MonitorQueryKeyArgs {
  entryId: UUID | ''
}

interface MonitorTargetSearchQueryKeyArgs {
  orgId: UUID | ''
  kind: MonitorRecipeTarget | ''
  query: string
  limit: number
}

export const monitorsQueryKeys = {
  all: () => ['monitors'] as const,
  recipes: (keyArgs: OrganizationMonitorsQueryKeyArgs) =>
    ['monitors', 'recipes', keyArgs.orgId] as const,
  // Org-list root — prefix-matches every org's monitor list so optimistic
  // unread-count patches reach all cached lists.
  lists: () => ['monitors', 'organization'] as const,
  list: (keyArgs: OrganizationMonitorsQueryKeyArgs) =>
    ['monitors', 'organization', keyArgs.orgId] as const,
  detail: (keyArgs: MonitorQueryKeyArgs) =>
    ['monitors', 'detail', keyArgs.monitorId] as const,
  feedDetail: (keyArgs: MonitorFeedDetailQueryKeyArgs) =>
    ['monitors', 'feed', keyArgs.monitorId, 'detail', keyArgs.entryId] as const,
  // Resource lineage/diff for one entry — its own leaf so per-user read/bookmark
  // mutations (which touch `feedDetail`) never refetch the resource history.
  feedLineage: (keyArgs: MonitorFeedDetailQueryKeyArgs) =>
    [
      'monitors',
      'feed',
      keyArgs.monitorId,
      'lineage',
      keyArgs.entryId,
    ] as const,
  // Org-wide feed root — its own cache root, separate from `feedDetail`/
  // `feedLineage` above, so per-row invalidations never clobber the org feed
  // list and vice-versa. Prefix-matches every filtered org feed list for the org.
  orgFeedRoot: (keyArgs: OrganizationMonitorsQueryKeyArgs) =>
    ['monitors', 'org-feed', keyArgs.orgId, 'list'] as const,
  orgFeedList: (keyArgs: OrgFeedListQueryKeyArgs) =>
    [
      'monitors',
      'org-feed',
      keyArgs.orgId,
      'list',
      {
        status: keyArgs.status,
        monitorId: keyArgs.monitorId,
        bookmarked: keyArgs.bookmarked,
        resourceKind: keyArgs.resourceKind,
        q: keyArgs.q,
        sort: keyArgs.sort,
      },
    ] as const,
  // Org-feed freshness root — its OWN cache root under the `org-feed-freshness`
  // namespace (NOT under `orgFeedRoot`). The freshness peek is a plain useQuery
  // holding a single page; the org-feed optimistic updaters assume
  // InfiniteData and prefix-match `orgFeedRoot`, so keeping freshness on a
  // distinct namespace guarantees they never match (and thus never corrupt) it.
  orgFeedFreshnessRoot: (keyArgs: OrganizationMonitorsQueryKeyArgs) =>
    ['monitors', 'org-feed-freshness', keyArgs.orgId] as const,
  orgFeedFreshness: (keyArgs: OrgFeedListQueryKeyArgs) =>
    [
      'monitors',
      'org-feed-freshness',
      keyArgs.orgId,
      {
        status: keyArgs.status,
        monitorId: keyArgs.monitorId,
        bookmarked: keyArgs.bookmarked,
        resourceKind: keyArgs.resourceKind,
        q: keyArgs.q,
        sort: keyArgs.sort,
      },
    ] as const,
  targetSearch: (keyArgs: MonitorTargetSearchQueryKeyArgs) =>
    [
      'monitors',
      'targets',
      'search',
      keyArgs.orgId,
      keyArgs.kind,
      keyArgs.query,
      keyArgs.limit,
    ] as const,
  slackChannels: (keyArgs: OrganizationMonitorsQueryKeyArgs) =>
    ['monitors', 'slack-channels', keyArgs.orgId] as const,
}

const MONITOR_FEED_ENTRY_STATUSES = new Set<MonitorFeedEntryStatus>([
  'inbox',
  'archived',
  'trashed',
])

const MONITOR_FEED_SORTS = new Set<MonitorFeedSort>(['recent', 'monitor'])

/**
 * Recover the org-feed filter from an org-feed-list query key. The filter lives
 * in an object at a fixed position (see `orgFeedList` above), so this is the
 * single place that knows the key shape. The status-eviction predicate reads
 * `status`/`bookmarked` from here to decide whether a moved row drops out of a
 * given cached list. Returns null when the key is not an org-feed-list key.
 */
export const orgFeedListFilterFromKey = (
  queryKey: QueryKey,
): OrgFeedListFilter | null => {
  if (
    queryKey[0] !== 'monitors' ||
    queryKey[1] !== 'org-feed' ||
    queryKey[3] !== 'list'
  ) {
    return null
  }
  const filter = queryKey[4]
  if (typeof filter !== 'object' || filter === null || Array.isArray(filter)) {
    return null
  }
  const record = filter as Record<string, unknown>
  const status = record.status
  const monitorId = record.monitorId
  const resourceKind = record.resourceKind
  const q = record.q
  const sort = record.sort
  return {
    status:
      typeof status === 'string' &&
      MONITOR_FEED_ENTRY_STATUSES.has(status as MonitorFeedEntryStatus)
        ? (status as MonitorFeedEntryStatus)
        : undefined,
    monitorId: typeof monitorId === 'string' ? (monitorId as UUID) : undefined,
    bookmarked: record.bookmarked === true ? true : undefined,
    resourceKind: typeof resourceKind === 'string' ? resourceKind : undefined,
    q: typeof q === 'string' ? q : undefined,
    sort:
      typeof sort === 'string' &&
      MONITOR_FEED_SORTS.has(sort as MonitorFeedSort)
        ? (sort as MonitorFeedSort)
        : 'recent',
  }
}
